import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface BrandingSettings {
  firmName: string;
  logoUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  fontColor: string;
  themePreset: ThemePreset;
  colorMode: ColorMode;
}

export interface DomainSettings {
  customDomain: string | null;
  verificationStatus: DomainVerificationStatus;
  verificationToken: string | null;
  sslStatus: SslStatus;
}

export type ThemePreset = 'default' | 'professional' | 'modern' | 'classic' | 'minimal';
export type ColorMode = 'light' | 'dark' | 'system';
export type DomainVerificationStatus = 'not_configured' | 'pending' | 'verified' | 'failed';
export type SslStatus = 'not_configured' | 'provisioning' | 'active' | 'expired';

export interface UpdateBrandingPayload {
  firmName: string;
  primaryColor: string;
  secondaryColor: string;
  fontColor: string;
  themePreset: ThemePreset;
  colorMode: ColorMode;
}

export interface UpdateDomainPayload {
  customDomain: string;
}

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const brandingKeys = {
  all: ['settings', 'branding'] as const,
  settings: () => [...brandingKeys.all, 'settings'] as const,
  domain: () => [...brandingKeys.all, 'domain'] as const,
};

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Fetch branding settings for the current tenant.
 */
export function useBrandingSettings() {
  return useQuery({
    queryKey: brandingKeys.settings(),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<BrandingSettings>>('/settings/branding');
      return response.data.data;
    },
  });
}

/**
 * Update branding settings (firm name, colors, theme, color mode).
 */
export function useUpdateBranding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateBrandingPayload) => {
      const response = await apiClient.put<ApiResponse<BrandingSettings>>(
        '/settings/branding',
        payload,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: brandingKeys.settings() });
    },
  });
}

/**
 * Upload a logo file (max 2MB, PNG or SVG).
 */
export function useUploadLogo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('logo', file);

      const response = await apiClient.post<ApiResponse<{ logoUrl: string }>>(
        '/settings/branding/logo',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: brandingKeys.settings() });
    },
  });
}

/**
 * Delete the current logo.
 */
export function useDeleteLogo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await apiClient.delete('/settings/branding/logo');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: brandingKeys.settings() });
    },
  });
}

/**
 * Fetch custom domain configuration.
 */
export function useDomainSettings() {
  return useQuery({
    queryKey: brandingKeys.domain(),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<DomainSettings>>(
        '/settings/branding/domain',
      );
      return response.data.data;
    },
  });
}

/**
 * Configure or update a custom domain.
 */
export function useUpdateDomain() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateDomainPayload) => {
      const response = await apiClient.put<ApiResponse<DomainSettings>>(
        '/settings/branding/domain',
        payload,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: brandingKeys.domain() });
    },
  });
}

/**
 * Remove the custom domain configuration.
 */
export function useDeleteDomain() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await apiClient.delete('/settings/branding/domain');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: brandingKeys.domain() });
    },
  });
}
